import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiBox, FiSearch, FiDownload, FiPrinter, FiExternalLink } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const formatMoney = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(amount);
};
const formatQty = (n) => {
  if (n === null || n === undefined || n === '') return '-';
  return new Intl.NumberFormat('en-PK', { maximumFractionDigits: 3 }).format(Number(n) || 0);
};
const formatDate = (v) => fmtDate(v) || '-';

const ZERO = { count: 0, plannedQty: 0, actualQty: 0, avgYield: 0, actualCost: 0, variance: 0 };

const ProductionSummary = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('production-summary');

  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'all');
  const [typeFilter, setTypeFilter] = useState(() => savedMem?.typeFilter ?? 'all');
  const [itemFilter, setItemFilter] = useState(() => savedMem?.itemFilter ?? 'all');
  const [bomFilter, setBomFilter] = useState(() => savedMem?.bomFilter ?? 'all');

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [rawPros, setRawPros] = useState(() => savedMem?.rawPros ?? []);

  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snapshot = await getDocs(query(collection(db, 'companies', activeCompany.id, 'financialYears'), orderBy('startDate', 'desc')));
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      // Keep the restored FY on a Back navigation; only default it on a fresh visit.
      if (!savedMem) {
        if (activeFinancialYear?.id) setSelectedFYId(activeFinancialYear.id);
        else if (fys.length > 0) setSelectedFYId(fys[0].id);
      }
    } catch (e) { console.error('Error loading financial years:', e); }
  }, [activeCompany, activeFinancialYear, savedMem]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);

  const generate = async () => {
    if (!activeCompany?.id) return;
    if (!selectedFYId) { alert('Please select a financial year'); return; }
    setLoading(true);
    setHasSearched(true);
    try {
      const txRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const snap = await getDocs(query(txRef, where('tranTypeCode', '==', 'PRO')));
      const pros = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => t.financialYearId === selectedFYId)
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return (t.branchId || null) === activeBranch.id;
        })
        .filter(t => {
          if (dateFrom && (t.docDate || '') < dateFrom) return false;
          if (dateTo && (t.docDate || '') > dateTo) return false;
          return true;
        })
        .sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setRawPros(pros);
    } catch (e) {
      console.error('Error generating production summary:', e);
      alert('Error generating report. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Remember this view (filters + loaded rows) once generated, so Back restores
  // it with 0 re-reads. Status/type/item/BOM filters run over already-loaded data,
  // so keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasSearched) return;
    saveMemory({
      rawPros, hasSearched: true,
      selectedFYId, dateFrom, dateTo,
      statusFilter, typeFilter, itemFilter, bomFilter,
    });
  }, [hasSearched, rawPros, selectedFYId, dateFrom, dateTo,
      statusFilter, typeFilter, itemFilter, bomFilter, saveMemory]);

  // Filter option lists derived from the fetched set
  const itemOptions = useMemo(() => {
    const m = new Map();
    rawPros.forEach(p => { if (p.finishedItemId && !m.has(p.finishedItemId)) m.set(p.finishedItemId, p.finishedItemName || p.finishedItemId); });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [rawPros]);
  const typeOptions = useMemo(() => [...new Set(rawPros.map(p => p.productionType).filter(Boolean))].sort(), [rawPros]);
  const bomOptions = useMemo(() => [...new Set(rawPros.map(p => p.bomDocNumber).filter(Boolean))].sort(), [rawPros]);

  const rows = useMemo(() => rawPros.filter(p => {
    if (statusFilter !== 'all' && (p.status || '') !== statusFilter) return false;
    if (typeFilter !== 'all' && (p.productionType || '') !== typeFilter) return false;
    if (itemFilter !== 'all' && (p.finishedItemId || '') !== itemFilter) return false;
    if (bomFilter !== 'all' && (p.bomDocNumber || '') !== bomFilter) return false;
    return true;
  }), [rawPros, statusFilter, typeFilter, itemFilter, bomFilter]);

  const summary = useMemo(() => {
    if (rows.length === 0) return ZERO;
    const completed = rows.filter(r => r.status === 'Completed');
    const yieldSum = completed.reduce((s, r) => s + (Number(r.yieldPercent) || 0), 0);
    return {
      count: rows.length,
      plannedQty: rows.reduce((s, r) => s + (Number(r.plannedQty) || 0), 0),
      actualQty: rows.reduce((s, r) => s + (Number(r.actualQty) || 0), 0),
      avgYield: completed.length ? yieldSum / completed.length : 0,
      actualCost: rows.reduce((s, r) => s + (Number(r.actualTotalCost) || 0), 0),
      variance: rows.reduce((s, r) => s + (Number(r.totalVariance) || 0), 0),
    };
  }, [rows]);

  const openPro = (id) => { if (id) navigate('/store/production-order', { state: { viewTransactionId: id } }); };

  const handleExport = () => {
    if (rows.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    let csv = `Production Summary / Register\nCompany: ${activeCompany?.companyName || ''}\nFinancial Year: ${fy?.yearName || fy?.yearCode || ''}\n`;
    if (dateFrom || dateTo) csv += `Period: ${dateFrom || 'start'} to ${dateTo || 'end'}\n`;
    csv += `\nPRO #,Date,BOM,Finished Item,Type,Planned Qty,Actual Qty,Yield %,Est Cost,Actual Cost,Total Variance,Status,Location\n`;
    rows.forEach(r => {
      csv += `${r.docNumber || ''},${formatDate(r.docDate)},${r.bomDocNumber || ''},"${(r.finishedItemName || '').replace(/"/g, '""')}",${r.productionType || ''},${r.plannedQty || 0},${r.actualQty || 0},${r.status === 'Completed' ? (Number(r.yieldPercent) || 0).toFixed(2) : ''},${r.estTotalCost || 0},${r.actualTotalCost || 0},${r.totalVariance || 0},${r.status || ''},"${(r.locationName || '').replace(/"/g, '""')}"\n`;
    });
    csv += `\nTotals,,,,,${summary.plannedQty},${summary.actualQty},${summary.avgYield.toFixed(2)},,${summary.actualCost},${summary.variance},,\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ProductionSummary.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (rows.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Production Summary</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:4px}
        .meta{color:#666;font-size:12px;margin-bottom:14px}
        .kpis{display:flex;flex-wrap:wrap;gap:18px;padding:14px;background:#f9f9f9;border-radius:8px;margin-bottom:12px}
        .kpi{text-align:center}.kpi .l{font-size:11px;color:#666}.kpi .v{font-size:15px;font-weight:600}
        table{width:100%;border-collapse:collapse}
        th{background:#800020;color:#fff;padding:7px;text-align:left;font-size:10px;text-transform:uppercase}
        td{padding:6px 7px;border-bottom:1px solid #eee}
        .amount{text-align:right;font-family:'Consolas',monospace}
        tr.total td{background:#efe6e9;font-weight:700}
        @media print{body{margin:12px}}
      </style></head><body>
      <h1>Production Summary / Register</h1>
      <div class="meta">${activeCompany?.companyName || ''} | FY: ${fy?.yearName || fy?.yearCode || ''}${(dateFrom || dateTo) ? ' | Period: ' + (dateFrom || 'start') + ' to ' + (dateTo || 'end') : ''}</div>
      <div class="kpis">
        <div class="kpi"><div class="l">Production Orders</div><div class="v">${summary.count}</div></div>
        <div class="kpi"><div class="l">Planned Qty</div><div class="v">${formatQty(summary.plannedQty)}</div></div>
        <div class="kpi"><div class="l">Actual Qty</div><div class="v">${formatQty(summary.actualQty)}</div></div>
        <div class="kpi"><div class="l">Avg Yield</div><div class="v">${summary.avgYield.toFixed(1)}%</div></div>
        <div class="kpi"><div class="l">Actual Cost</div><div class="v">${formatMoney(summary.actualCost)}</div></div>
        <div class="kpi"><div class="l">Total Variance</div><div class="v">${formatMoney(summary.variance)}</div></div>
      </div>
      <table>
        <thead><tr><th>PRO #</th><th>Date</th><th>BOM</th><th>Finished Item</th><th>Type</th><th class="amount">Planned</th><th class="amount">Actual</th><th class="amount">Yield %</th><th class="amount">Est Cost</th><th class="amount">Actual Cost</th><th class="amount">Variance</th><th>Status</th><th>Location</th></tr></thead>
        <tbody>
          ${rows.map(r => `<tr>
            <td>${r.docNumber || '-'}</td><td>${formatDate(r.docDate)}</td><td>${r.bomDocNumber || '-'}</td>
            <td>${r.finishedItemName || '-'}</td><td>${r.productionType || '-'}</td>
            <td class="amount">${formatQty(r.plannedQty)}</td>
            <td class="amount">${r.status === 'Completed' ? formatQty(r.actualQty) : '-'}</td>
            <td class="amount">${r.status === 'Completed' ? (Number(r.yieldPercent) || 0).toFixed(1) + '%' : '-'}</td>
            <td class="amount">${formatMoney(r.estTotalCost)}</td>
            <td class="amount">${r.status === 'Completed' ? formatMoney(r.actualTotalCost) : '-'}</td>
            <td class="amount">${r.status === 'Completed' ? formatMoney(r.totalVariance) : '-'}</td>
            <td>${r.status || '-'}</td><td>${r.locationName || '-'}</td>
          </tr>`).join('')}
          <tr class="total"><td colspan="5">Totals</td><td class="amount">${formatQty(summary.plannedQty)}</td><td class="amount">${formatQty(summary.actualQty)}</td><td class="amount">${summary.avgYield.toFixed(1)}%</td><td class="amount"></td><td class="amount">${formatMoney(summary.actualCost)}</td><td class="amount">${formatMoney(summary.variance)}</td><td></td><td></td></tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Production Summary / Register" breadcrumb="Reports / Production Reports / Production Summary">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year *</Label>
            <Select value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="Planned">Planned</option>
              <option value="Completed">Completed</option>
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Finished Item</Label>
            <Select value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
              <option value="all">All Items</option>
              {itemOptions.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>BOM</Label>
            <Select value={bomFilter} onChange={(e) => setBomFilter(e.target.value)}>
              <option value="all">All BOMs</option>
              {bomOptions.map(b => <option key={b} value={b}>{b}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Production Type</Label>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>From Date</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </FilterGroup>
        </FiltersGrid>
        <ButtonGroup>
          <Button onClick={handleExport} disabled={rows.length === 0}><FiDownload /> Export CSV</Button>
          <Button onClick={handlePrint} disabled={rows.length === 0}><FiPrinter /> Print</Button>
          <Button primary onClick={generate} disabled={loading}><FiSearch /> {loading ? 'Loading...' : 'Generate Report'}</Button>
        </ButtonGroup>
      </FiltersCard>

      {hasSearched && !loading && rawPros.length > 0 && (
        <KpiGrid>
          <Kpi><KpiLabel>Production Orders</KpiLabel><KpiValue>{summary.count}</KpiValue></Kpi>
          <Kpi><KpiLabel>Planned Qty</KpiLabel><KpiValue>{formatQty(summary.plannedQty)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Actual Qty</KpiLabel><KpiValue>{formatQty(summary.actualQty)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Avg Yield</KpiLabel><KpiValue>{summary.avgYield.toFixed(1)}%</KpiValue></Kpi>
          <Kpi><KpiLabel>Actual Cost</KpiLabel><KpiValue>{formatMoney(summary.actualCost)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Total Variance</KpiLabel><KpiValue $pos={summary.variance <= 0} $neg={summary.variance > 0}>{formatMoney(summary.variance)}</KpiValue></Kpi>
        </KpiGrid>
      )}

      <ContentCard>
        {!hasSearched ? (
          <EmptyState>
            <EmptyIcon><FiBox /></EmptyIcon>
            <EmptyText>Select a financial year and generate to view production orders</EmptyText>
          </EmptyState>
        ) : loading ? (
          <EmptyState><EmptyText>Loading...</EmptyText></EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState>
            <EmptyIcon><FiBox /></EmptyIcon>
            <EmptyText>No production orders match the selected filters</EmptyText>
          </EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>PRO #</Th>
                  <Th>Date</Th>
                  <Th>BOM</Th>
                  <Th>Finished Item</Th>
                  <Th>Type</Th>
                  <Th align="right">Planned</Th>
                  <Th align="right">Actual</Th>
                  <Th align="right">Yield %</Th>
                  <Th align="right">Est Cost</Th>
                  <Th align="right">Actual Cost</Th>
                  <Th align="right">Variance</Th>
                  <Th>Status</Th>
                  <Th>Location</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map(r => {
                  const completed = r.status === 'Completed';
                  return (
                    <Tr key={r.id}>
                      <Td>
                        <DocNumber onClick={() => openPro(r.id)} title="Open production order">
                          {r.docNumber || '-'} <FiExternalLink size={11} />
                        </DocNumber>
                      </Td>
                      <Td>{formatDate(r.docDate)}</Td>
                      <Td>{r.bomDocNumber || '-'}</Td>
                      <Td>{r.finishedItemName || '-'}</Td>
                      <Td>{r.productionType || '-'}</Td>
                      <AmountCell align="right">{formatQty(r.plannedQty)}</AmountCell>
                      <AmountCell align="right">{completed ? formatQty(r.actualQty) : '-'}</AmountCell>
                      <AmountCell align="right">{completed ? `${(Number(r.yieldPercent) || 0).toFixed(1)}%` : '-'}</AmountCell>
                      <AmountCell align="right">{formatMoney(r.estTotalCost)}</AmountCell>
                      <AmountCell align="right">{completed ? formatMoney(r.actualTotalCost) : '-'}</AmountCell>
                      <AmountCell align="right" $pos={completed && (Number(r.totalVariance) || 0) <= 0} $neg={completed && (Number(r.totalVariance) || 0) > 0}>
                        {completed ? formatMoney(r.totalVariance) : '-'}
                      </AmountCell>
                      <Td><StatusBadge $status={r.status}>{r.status || '-'}</StatusBadge></Td>
                      <Td>{r.locationName || '-'}</Td>
                    </Tr>
                  );
                })}
                <Tr $total>
                  <Td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>Totals</Td>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatQty(summary.plannedQty)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatQty(summary.actualQty)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{summary.avgYield.toFixed(1)}%</AmountCell>
                  <AmountCell align="right">-</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatMoney(summary.actualCost)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatMoney(summary.variance)}</AmountCell>
                  <Td></Td>
                  <Td></Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </ContentCard>
    </PageLayout>
  );
};

// ---- Styled Components ----
const FiltersCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; padding: 20px; margin-bottom: 20px;
`;
const FiltersGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 16px;
`;
const FilterGroup = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`;
const Label = styled.label`
  font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase;
`;
const Select = styled.select`
  padding: 10px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; color: ${colors.text.primary}; background: white; cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const Input = styled.input`
  padding: 10px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const ButtonGroup = styled.div`
  display: flex; gap: 12px; justify-content: flex-end;
`;
const Button = styled.button`
  padding: 10px 20px; border: none; border-radius: ${colors.borderRadius.md}; font-size: 14px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.2s;
  ${props => props.primary ? `
    background: ${colors.primary.main}; color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: ${colors.neutral[100]}; color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[200]}; }
  `}
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const KpiGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; margin-bottom: 20px;
`;
const Kpi = styled.div`
  background: white; padding: 16px; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm};
`;
const KpiLabel = styled.div`
  font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 6px;
`;
const KpiValue = styled.div`
  font-size: 20px; font-weight: 600; color: ${props => props.$pos ? '#2E7D32' : props.$neg ? '#C62828' : colors.text.primary};
`;
const ContentCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; overflow: hidden;
`;
const TableContainer = styled.div`
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse;
`;
const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;
const Th = styled.th`
  padding: 12px 14px; text-align: ${props => props.align || 'left'}; font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid ${colors.neutral[200]}; white-space: nowrap;
`;
const Tbody = styled.tbody``;
const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
  ${props => props.$total && `background: ${colors.primary.main}0D; font-weight: 600;`}
`;
const Td = styled.td`
  padding: 10px 14px; font-size: 13px; color: ${colors.text.primary}; text-align: ${props => props.align || 'left'}; white-space: nowrap;
`;
const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace; font-size: 13px; text-align: right;
  color: ${props => props.$pos ? '#2E7D32' : props.$neg ? '#C62828' : colors.text.primary};
`;
const DocNumber = styled.span`
  font-weight: 500; color: ${colors.primary.main}; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;
  &:hover { text-decoration: underline; }
  svg { opacity: 0.6; }
`;
const StatusBadge = styled.span`
  display: inline-block; padding: 3px 8px; border-radius: ${colors.borderRadius.sm}; font-size: 11px; font-weight: 500;
  background: ${props => {
    const s = props.$status;
    if (s === 'Completed') return '#E8F5E9';
    if (s === 'Planned') return '#FFF3E0';
    if (s === 'Cancelled') return '#FFEBEE';
    return colors.neutral[200];
  }};
  color: ${props => {
    const s = props.$status;
    if (s === 'Completed') return '#2E7D32';
    if (s === 'Planned') return '#E65100';
    if (s === 'Cancelled') return '#C62828';
    return colors.text.secondary;
  }};
`;
const EmptyState = styled.div`
  text-align: center; padding: 60px 20px; color: ${colors.text.secondary};
`;
const EmptyIcon = styled.div`
  font-size: 48px; color: ${colors.neutral[300]}; margin-bottom: 16px;
`;
const EmptyText = styled.p`
  font-size: 16px; margin-bottom: 8px;
`;

export default ProductionSummary;
